import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Play, Pause, Settings, Sparkles, Brain,
  CheckCircle2, AlertCircle, RefreshCw, Zap, TrendingUp,
  Users, DollarSign, Bell, ChevronRight, Plus, Trash2,
  CalendarClock, CalendarCheck, Power, Edit2, Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Progress } from '../../ui/progress';
import { Separator } from '../../ui/separator';
import { db } from '../../../lib/db';
import { formatUSDCurrency, PAY_FREQUENCIES } from '../../../lib/usPayrollCalculations';
import { formatDate } from '../../../lib/dateUtils';
import { aiService } from '../../../lib/aiService';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

const AUTOMATION_STATUS = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  paused: { label: 'Paused', color: 'bg-amber-100 text-amber-700', icon: Pause },
  disabled: { label: 'Disabled', color: 'bg-gray-100 text-gray-700', icon: Power }
};

const NOTIFICATION_TRIGGERS = [
  { value: 'before_run', label: 'Before Payroll Run' },
  { value: 'after_run', label: 'After Payroll Completes' },
  { value: 'approval_needed', label: 'Approval Required' },
  { value: 'error', label: 'On Error' }
];

export default function PayrollAutomation() {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [payGroups, setPayGroups] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showAiRecommendations, setShowAiRecommendations] = useState(false);

  // New schedule form
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    payGroupId: '',
    frequency: 'biweekly',
    dayOfWeek: 'friday',
    dayOfMonth: 15,
    time: '08:00',
    autoApprove: false,
    autoSubmit: false,
    notifyBefore: 24,
    notifyEmails: '',
    status: 'active'
  });

  // Summary stats
  const [stats, setStats] = useState({
    activeSchedules: 0,
    nextRunDate: null,
    totalAutomatedRuns: 0,
    totalPayrollRuns: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [groups, runs, storedSchedules] = await Promise.all([
        db.payGroups.toArray(),
        db.payrollRuns.toArray(),
        db.payrollSchedules?.toArray() || Promise.resolve([])
      ]);

      setPayGroups(groups);
      setPayrollHistory(runs.sort((a, b) => b.createdAt - a.createdAt).slice(0, 10));
      setSchedules(storedSchedules);

      // Calculate stats
      const active = storedSchedules.filter(s => s.status === 'active').length;
      const automatedRuns = runs.filter(r => r.automated).length;
      
      // Calculate next run date from active schedules
      let nextRun = null;
      storedSchedules.filter(s => s.status === 'active').forEach(schedule => {
        const nextDate = calculateNextRunDate(schedule);
        if (!nextRun || nextDate < nextRun) {
          nextRun = nextDate;
        }
      });

      setStats({
        activeSchedules: active,
        nextRunDate: nextRun,
        totalAutomatedRuns: automatedRuns,
        totalPayrollRuns: runs.length
      });

    } catch (error) {
      console.error('Error loading automation data:', error);
      toast.error('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  };

  const calculateNextRunDate = (schedule) => {
    const now = new Date();
    let nextDate = new Date();
    
    if (schedule.frequency === 'weekly' || schedule.frequency === 'biweekly') {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const targetDay = days.indexOf(schedule.dayOfWeek.toLowerCase());
      const currentDay = now.getDay();
      let daysUntil = targetDay - currentDay;
      if (daysUntil <= 0) daysUntil += 7;
      if (schedule.frequency === 'biweekly') daysUntil += 7;
      nextDate.setDate(now.getDate() + daysUntil);
    } else if (schedule.frequency === 'semimonthly') {
      // 15th and last day of month
      if (now.getDate() < 15) {
        nextDate.setDate(15);
      } else {
        nextDate.setMonth(now.getMonth() + 1, 0); // Last day of current month
      }
    } else if (schedule.frequency === 'monthly') {
      nextDate.setDate(schedule.dayOfMonth || 15);
      if (nextDate <= now) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
    }

    // Set time
    const [hours, minutes] = (schedule.time || '08:00').split(':');
    nextDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    return nextDate;
  };

  const saveSchedule = async () => {
    if (!scheduleForm.name || !scheduleForm.payGroupId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const scheduleData = {
        ...scheduleForm,
        id: editingSchedule?.id || uuidv4(),
        notifyEmails: scheduleForm.notifyEmails.split(',').map(e => e.trim()).filter(e => e),
        updatedAt: Date.now()
      };

      if (!editingSchedule) {
        scheduleData.createdAt = Date.now();
      }

      // Check if table exists, create if not
      if (!db.payrollSchedules) {
        // Add table dynamically or handle gracefully
        toast.error('Payroll schedules table not configured');
        return;
      }

      await db.payrollSchedules.put(scheduleData);
      
      toast.success(editingSchedule ? 'Schedule updated' : 'Schedule created');
      setShowScheduleModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    }
  };

  const deleteSchedule = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Schedule',
      description: 'Are you sure you want to delete this schedule?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (!confirmed) return;
    
    try {
      await db.payrollSchedules.delete(id);
      toast.success('Schedule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const toggleScheduleStatus = async (schedule) => {
    const newStatus = schedule.status === 'active' ? 'paused' : 'active';
    try {
      await db.payrollSchedules.update(schedule.id, { status: newStatus });
      toast.success(`Schedule ${newStatus === 'active' ? 'activated' : 'paused'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const resetForm = () => {
    setScheduleForm({
      name: '',
      payGroupId: '',
      frequency: 'biweekly',
      dayOfWeek: 'friday',
      dayOfMonth: 15,
      time: '08:00',
      autoApprove: false,
      autoSubmit: false,
      notifyBefore: 24,
      notifyEmails: '',
      status: 'active'
    });
    setEditingSchedule(null);
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    // Get pay group settings for this schedule
    const selectedGroup = payGroups.find(g => g.id === schedule.payGroupId);
    setScheduleForm({
      ...schedule,
      frequency: selectedGroup?.frequency || schedule.frequency,
      dayOfWeek: selectedGroup?.payDay || schedule.dayOfWeek,
      notifyEmails: schedule.notifyEmails?.join(', ') || ''
    });
    setShowScheduleModal(true);
  };

  // AI-Powered Analysis
  const getAiRecommendations = async () => {
    setAiLoading(true);
    try {
      // Gather payroll data for AI analysis
      const [runs, employees, compensation] = await Promise.all([
        db.payrollRuns.toArray(),
        db.users.where('role').notEqual('admin').toArray(),
        db.employeeCompensation.toArray()
      ]);

      const analysisData = {
        totalEmployees: employees.length,
        payGroups: payGroups.map(g => ({ name: g.name, frequency: g.frequency })),
        recentRuns: runs.slice(-10).map(r => ({
          date: formatDate(new Date(r.createdAt)),
          status: r.status,
          totalNet: r.totalNet || r.summary?.totalNet || 0
        })),
        schedules: schedules.map(s => ({
          name: s.name,
          frequency: s.frequency,
          status: s.status,
          autoApprove: s.autoApprove
        })),
        compensationSummary: {
          salaryCount: compensation.filter(c => c.payType === 'salary').length,
          hourlyCount: compensation.filter(c => c.payType === 'hourly').length,
          avgPayRate: compensation.reduce((sum, c) => sum + (c.payRate || 0), 0) / compensation.length
        }
      };

      // Call AI service for recommendations
      const insights = await aiService.analyzePayroll({
        type: 'automation_recommendations',
        data: analysisData,
        request: 'Analyze the payroll setup and provide recommendations for automation, scheduling optimization, and potential issues. Focus on: 1) Optimal schedule timing, 2) Risk areas, 3) Efficiency improvements, 4) Compliance considerations.'
      });

      setAiInsights(insights);
      setShowAiRecommendations(true);
    } catch (error) {
      console.error('AI analysis error:', error);
      // Use fallback recommendations
      setAiInsights(generateFallbackRecommendations());
      setShowAiRecommendations(true);
    } finally {
      setAiLoading(false);
    }
  };

  const generateFallbackRecommendations = () => ({
    summary: {
      totalPayroll: 0,
      averagePay: 0,
      trend: 'stable'
    },
    optimizations: [
      {
        title: 'Enable Auto-Approval for Regular Runs',
        description: 'For established pay groups with consistent headcount, enable auto-approval to reduce manual steps.',
        priority: 'medium',
        potentialSavings: 120 // minutes per month
      },
      {
        title: 'Schedule Pre-Check 2 Days Before',
        description: 'Run automated pre-checks 48 hours before payroll to catch issues early.',
        priority: 'high',
        potentialSavings: 60
      },
      {
        title: 'Consolidate Pay Groups',
        description: 'Consider consolidating pay groups with similar frequencies to simplify management.',
        priority: 'low',
        potentialSavings: 30
      }
    ],
    compliance: [
      {
        issue: 'Tax Filing Deadlines',
        recommendation: 'Set up automated reminders for quarterly tax filings.',
        severity: 'warning'
      }
    ],
    forecast: {
      nextMonth: 0,
      yearEnd: 0,
      recommendation: 'Maintain current automation schedule with quarterly reviews.'
    }
  });

  const runPayrollNow = async (schedule) => {
    toast.info(`Starting payroll for ${schedule.name}...`);
    // Navigate to payroll run page with pre-filled settings
    window.location.href = `/admin/payroll/us/run?payGroupId=${schedule.payGroupId}&scheduled=true`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-teal-600" />
            Payroll Automation
          </h1>
          <p className="text-gray-500 mt-1">Schedule and automate payroll runs with AI assistance</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={getAiRecommendations}
            disabled={aiLoading}
          >
            {aiLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Brain className="w-4 h-4 mr-2" />
            )}
            AI Recommendations
          </Button>
          <Button onClick={() => { resetForm(); setShowScheduleModal(true); }}>
            <Plus className="w-4 h-4 mr-2" /> New Schedule
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Schedules</p>
                <p className="text-2xl font-bold text-teal-600">{stats.activeSchedules}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <CalendarClock className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Next Run</p>
                <p className="text-lg font-bold text-gray-900">
                  {stats.nextRunDate ? formatDate(stats.nextRunDate) : 'Not scheduled'}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Automated Runs</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.totalAutomatedRuns}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Runs</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalPayrollRuns}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Insights Banner */}
      {aiService.isConfigured() && (
        <Alert className="bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
          <Sparkles className="h-4 w-4 text-teal-600" />
          <AlertTitle className="text-teal-800">AI-Powered Automation</AlertTitle>
          <AlertDescription className="text-teal-700">
            Get intelligent recommendations for optimizing your payroll schedules, reducing errors, and ensuring compliance.
            <Button 
              variant="link" 
              className="p-0 ml-2 text-teal-700 underline"
              onClick={getAiRecommendations}
            >
              Analyze Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="schedules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="history">Run History</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="schedules">
          <Card>
            <CardHeader>
              <CardTitle>Payroll Schedules</CardTitle>
              <CardDescription>Configure automated payroll processing</CardDescription>
            </CardHeader>
            <CardContent>
              {schedules.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarClock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedules Yet</h3>
                  <p className="text-gray-500 mb-4">
                    Create your first automated payroll schedule to save time and ensure consistency.
                  </p>
                  <Button onClick={() => { resetForm(); setShowScheduleModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Create Schedule
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Schedule Name</TableHead>
                      <TableHead>Pay Group</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Next Run</TableHead>
                      <TableHead>Auto-Approve</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map(schedule => {
                      const payGroup = payGroups.find(g => g.id === schedule.payGroupId);
                      const StatusIcon = AUTOMATION_STATUS[schedule.status]?.icon || Clock;
                      const nextRun = calculateNextRunDate(schedule);

                      return (
                        <TableRow key={schedule.id}>
                          <TableCell className="font-medium">{schedule.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{payGroup?.name || 'Unknown'}</Badge>
                          </TableCell>
                          <TableCell className="capitalize">{schedule.frequency}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(nextRun)}
                              <span className="text-gray-400 text-sm">{schedule.time}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {schedule.autoApprove ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge>
                            ) : (
                              <Badge variant="outline">Manual</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={AUTOMATION_STATUS[schedule.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {AUTOMATION_STATUS[schedule.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runPayrollNow(schedule)}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleScheduleStatus(schedule)}
                              >
                                {schedule.status === 'active' ? 
                                  <Pause className="w-4 h-4" /> : 
                                  <Power className="w-4 h-4" />
                                }
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditModal(schedule)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => deleteSchedule(schedule.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Recent Payroll Runs</CardTitle>
              <CardDescription>History of automated and manual payroll runs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead className="text-right">Total Net</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Automated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No payroll history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollHistory.map(run => (
                      <TableRow key={run.id}>
                        <TableCell>{formatDate(new Date(run.createdAt))}</TableCell>
                        <TableCell className="capitalize">{run.runType || run.payFrequency || 'regular'}</TableCell>
                        <TableCell>{run.employeeCount || '-'}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatUSDCurrency(run.totalNet || run.summary?.totalNet || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            run.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            run.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {run.automated ? (
                            <Badge className="bg-purple-100 text-purple-700">
                              <Zap className="w-3 h-3 mr-1" /> Auto
                            </Badge>
                          ) : (
                            <span className="text-gray-400">Manual</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Automation Settings</CardTitle>
              <CardDescription>Global settings for payroll automation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium">Notifications</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Email notifications</Label>
                      <p className="text-sm text-gray-500">Send email alerts for payroll events</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Pre-run reminders</Label>
                      <p className="text-sm text-gray-500">Notify before scheduled runs</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Error alerts</Label>
                      <p className="text-sm text-gray-500">Immediate notification on failures</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium">Processing</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto pre-check</Label>
                      <p className="text-sm text-gray-500">Run validation before processing</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>AI anomaly detection</Label>
                      <p className="text-sm text-gray-500">Flag unusual payroll items</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto tax filing reminders</Label>
                      <p className="text-sm text-gray-500">Remind about upcoming deadlines</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule ? 'Edit Schedule' : 'Create Payroll Schedule'}
            </DialogTitle>
            <DialogDescription>
              Set up automated payroll processing for consistent, on-time payments
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Schedule Name *</Label>
              <Input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({...scheduleForm, name: e.target.value})}
                placeholder="e.g., Bi-Weekly Staff Payroll"
              />
            </div>

            <div className="space-y-2">
              <Label>Pay Group *</Label>
              <Select 
                value={scheduleForm.payGroupId} 
                onValueChange={(v) => {
                  const selectedGroup = payGroups.find(g => g.id === v);
                  setScheduleForm({
                    ...scheduleForm, 
                    payGroupId: v,
                    frequency: selectedGroup?.frequency || 'biweekly',
                    dayOfWeek: selectedGroup?.payDay || 'friday'
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select pay group" />
                </SelectTrigger>
                <SelectContent>
                  {payGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} ({PAY_FREQUENCIES[group.frequency]?.label || group.frequency})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show pay group details when selected */}
            {scheduleForm.payGroupId && (
              <div className="col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">Pay Group Settings (from selected pay group)</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600">Frequency:</span>{' '}
                    <span className="font-medium">{PAY_FREQUENCIES[scheduleForm.frequency]?.label || scheduleForm.frequency}</span>
                  </div>
                  <div>
                    <span className="text-blue-600">Pay Day:</span>{' '}
                    <span className="font-medium capitalize">{scheduleForm.dayOfWeek}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Run Time</Label>
              <Input
                type="time"
                value={scheduleForm.time}
                onChange={(e) => setScheduleForm({...scheduleForm, time: e.target.value})}
              />
              <p className="text-xs text-gray-500">Time to start automated payroll run</p>
            </div>

            <div className="space-y-2">
              <Label>Notify Before (hours)</Label>
              <Input
                type="number"
                min="0"
                max="72"
                value={scheduleForm.notifyBefore}
                onChange={(e) => setScheduleForm({...scheduleForm, notifyBefore: parseInt(e.target.value)})}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Notification Emails</Label>
              <Input
                value={scheduleForm.notifyEmails}
                onChange={(e) => setScheduleForm({...scheduleForm, notifyEmails: e.target.value})}
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-gray-500">Comma-separated list of email addresses</p>
            </div>

            <div className="col-span-2 border-t pt-4 space-y-4">
              <h4 className="font-medium">Automation Options</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Approve</Label>
                  <p className="text-sm text-gray-500">Automatically approve payroll after calculation</p>
                </div>
                <Switch 
                  checked={scheduleForm.autoApprove}
                  onCheckedChange={(v) => setScheduleForm({...scheduleForm, autoApprove: v})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Submit</Label>
                  <p className="text-sm text-gray-500">Submit payroll without manual review (use with caution)</p>
                </div>
                <Switch 
                  checked={scheduleForm.autoSubmit}
                  onCheckedChange={(v) => setScheduleForm({...scheduleForm, autoSubmit: v})}
                />
              </div>

              {scheduleForm.autoSubmit && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Auto-submit will process and finalize payroll without manual approval. 
                    Ensure all employee data is accurate before enabling.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowScheduleModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={saveSchedule}>
              <Save className="w-4 h-4 mr-2" />
              {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Recommendations Modal */}
      <Dialog open={showAiRecommendations} onOpenChange={setShowAiRecommendations}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-teal-600" />
              AI Payroll Recommendations
            </DialogTitle>
            <DialogDescription>
              Intelligent insights to optimize your payroll automation
            </DialogDescription>
          </DialogHeader>

          {aiInsights && (
            <div className="space-y-6 py-4">
              {/* Optimizations */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  Optimization Opportunities
                </h3>
                <div className="space-y-3">
                  {aiInsights.optimizations?.map((opt, i) => (
                    <Card key={i} className="border-l-4 border-l-emerald-500">
                      <CardContent className="py-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{opt.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{opt.description}</p>
                          </div>
                          <Badge className={
                            opt.priority === 'high' ? 'bg-red-100 text-red-700' :
                            opt.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {opt.priority}
                          </Badge>
                        </div>
                        {opt.potentialSavings && (
                          <p className="text-sm text-emerald-600 mt-2">
                            Potential time savings: {opt.potentialSavings} minutes/month
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Compliance */}
              {aiInsights.compliance?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    Compliance Considerations
                  </h3>
                  <div className="space-y-2">
                    {aiInsights.compliance.map((item, i) => (
                      <Alert key={i} className={
                        item.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }>
                        <AlertTitle className={item.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}>
                          {item.issue}
                        </AlertTitle>
                        <AlertDescription className={item.severity === 'critical' ? 'text-red-700' : 'text-amber-700'}>
                          {item.recommendation}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}

              {/* Forecast */}
              {aiInsights.forecast && (
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Recommendation
                  </h3>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="py-4">
                      <p className="text-purple-800">{aiInsights.forecast.recommendation}</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowAiRecommendations(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
